import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "./ScrollEffects";
import { Tilt3DCard } from "./Tilt3DCard";
import { WordReveal } from "./TextEffects";
import { Star } from "lucide-react";

const AVATAR_GRADIENTS = [
  "from-[hsl(20,100%,60%)] to-[hsl(35,80%,65%)]",
  "from-[hsl(35,80%,55%)] to-[hsl(45,90%,62%)]",
  "from-[hsl(230,40%,65%)] to-[hsl(260,35%,70%)]",
  "from-[hsl(150,40%,55%)] to-[hsl(170,35%,60%)]",
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
  },
};

interface TestimonialItem {
  name: string;
  pet: string;
  text: string;
  emoji: string;
  initials: string;
}

const TestimonialCard: React.FC<TestimonialItem & { avatarGradient: string }> = ({ name, pet, text, emoji, initials, avatarGradient }) => (
  <Tilt3DCard className="glass rounded-3xl p-7 border border-border/50 shadow-warm-sm h-full cursor-default relative overflow-hidden group">
    {/* Hover glow */}
    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    
    <span
      className="absolute top-2 left-4 text-7xl font-serif leading-none pointer-events-none select-none"
      style={{ color: "hsl(var(--primary) / 0.08)" }}
    >
      "
    </span>
    <div className="relative z-10">
      <div className="flex items-center gap-3 mb-4">
        <motion.div 
          className={`w-12 h-12 rounded-full bg-gradient-to-br ${avatarGradient} flex items-center justify-center shrink-0 ring-2 ring-background`}
          whileHover={{ scale: 1.1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <span className="text-sm font-bold text-white">{initials}</span>
        </motion.div>
        <motion.span
          className="text-2xl"
          whileHover={{ scale: 1.3 }}
          transition={{ type: "spring", stiffness: 400 }}
        >
          {emoji}
        </motion.span>
      </div>
      
      {/* Star rating */}
      <div className="flex gap-0.5 mb-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} size={12} className="fill-primary text-primary" />
        ))}
      </div>
      
      <p className="text-foreground text-sm leading-relaxed mb-5 italic">«{text}»</p>
      <div className="border-t border-border/50 pt-4">
        <p className="font-bold text-foreground text-sm">{name}</p>
        <p className="text-muted-foreground text-xs">{pet}</p>
      </div>
    </div>
  </Tilt3DCard>
);

export const TestimonialsSection: React.FC = () => {
  const { t } = useTranslation();
  const items = t('testimonials.items', { returnObjects: true }) as TestimonialItem[];

  return (
    <section className="py-16 sm:py-24 px-4 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      {/* Subtle bg */}
      <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse 60% 50% at 50% 50%, hsl(var(--primary) / 0.03) 0%, transparent 70%)",
      }} />
      
      <div className="max-w-6xl mx-auto relative z-10">
        <ScrollReveal className="text-center mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-4">
            {t('testimonials.section_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-4">
            <WordReveal
              text={`${t('testimonials.section_title_prefix')} ${t('testimonials.section_title_highlight')}`}
              highlightWords={t('testimonials.section_title_highlight').split(' ')}
            />
          </h2>
        </ScrollReveal>

        {/* Desktop: grid */}
        <motion.div
          className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-5"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={containerVariants}
        >
          {Array.isArray(items) && items.map((item, i) => (
            <motion.div key={i} variants={itemVariants}>
              <TestimonialCard {...item} avatarGradient={AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length]} />
            </motion.div>
          ))}
        </motion.div>

        {/* Mobile: horizontal scroll */}
        <div className="md:hidden -mx-4 px-4">
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-hide">
            {Array.isArray(items) && items.map((item, i) => (
              <div key={i} className="snap-center shrink-0 w-[280px]">
                <TestimonialCard {...item} avatarGradient={AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length]} />
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-1.5 mt-3">
            {Array.isArray(items) && items.map((_, i) => (
              <div key={i} className="w-1.5 h-1.5 rounded-full bg-primary/20" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
